// Browser Sentry init. Reads the DSN from a SSR-injected window global
// (`window.__SENTRY_DSN__`) so we don't need a separate VITE_ env var.
// Safe to call multiple times — guards initialization.

import * as Sentry from "@sentry/react";

let initialized = false;

export function getRelease() {
  return (
    (import.meta.env.VITE_APP_RELEASE as string | undefined) ||
    (typeof window !== "undefined"
      ? (window as unknown as { __APP_RELEASE__?: string }).__APP_RELEASE__
      : undefined) ||
    "aros-flow@dev"
  );
}

/** Non-sensitive device/runtime facts attached to every event and bug report. */
export function getDeviceInfo() {
  if (typeof window === "undefined") return {};
  const nav = window.navigator;
  return {
    userAgent: nav.userAgent,
    language: nav.language,
    platform: (nav as unknown as { platform?: string }).platform ?? "unknown",
    viewport: `${window.innerWidth}x${window.innerHeight}`,
    screen: `${window.screen?.width ?? 0}x${window.screen?.height ?? 0}`,
    devicePixelRatio: window.devicePixelRatio,
    touch: "ontouchstart" in window || nav.maxTouchPoints > 0,
    formFactor:
      window.innerWidth < 768 ? "mobile" : window.innerWidth < 1024 ? "tablet" : "desktop",
    online: nav.onLine,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    url: window.location.href,
    release: getRelease(),
  } as Record<string, unknown>;
}

export function initBrowserSentry() {
  if (initialized || typeof window === "undefined") return;
  const dsn =
    (window as unknown as { __SENTRY_DSN__?: string }).__SENTRY_DSN__ ||
    (import.meta.env.VITE_SENTRY_DSN as string | undefined);
  if (!dsn) return;
  Sentry.init({
    dsn,
    release: getRelease(),
    environment: import.meta.env.MODE,
    tracesSampleRate: 0.1,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    integrations: [Sentry.browserTracingIntegration()],
  });
  const info = getDeviceInfo();
  Sentry.setTag("form_factor", String(info.formFactor));
  Sentry.setTag("viewport", String(info.viewport));
  Sentry.setContext("device", info);
  // Expose for non-React capture paths (ErrorBoundary).
  (window as unknown as { Sentry?: typeof Sentry }).Sentry = Sentry;
  initialized = true;
}

export function captureBrowserException(error: unknown, context?: Record<string, unknown>) {
  if (typeof window === "undefined") return undefined;
  if (!initialized) initBrowserSentry();
  if (!initialized) return undefined;
  return Sentry.captureException(error, { extra: { ...getDeviceInfo(), ...context } });
}

/** Send a user-filed bug report (with optional screenshot attachment) to Sentry. */
export function captureBugReport(payload: {
  message: string;
  steps: string[];
  device: Record<string, unknown>;
  screenshotDataUrl?: string | null;
}) {
  if (typeof window === "undefined") return undefined;
  if (!initialized) initBrowserSentry();
  if (!initialized) return undefined;
  return Sentry.withScope((scope) => {
    scope.setLevel("warning");
    scope.setTag("report_type", "user_bug_report");
    scope.setContext("device", payload.device);
    scope.setContext("steps", { taken: payload.steps });
    if (payload.screenshotDataUrl) {
      try {
        const base64 = payload.screenshotDataUrl.split(",")[1] ?? "";
        const bin = atob(base64);
        const bytes = new Uint8Array(bin.length);
        for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
        scope.addAttachment({
          filename: "screenshot.png",
          data: bytes,
          contentType: "image/png",
        });
      } catch {
        /* attachment is best-effort */
      }
    }
    return Sentry.captureMessage(`[Bug report] ${payload.message}`, "warning");
  });
}
